import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { ArrowUpRight, Check, PlayCircle } from 'lucide-react';
import styles from './Hero.module.css';
import Button from '../Button/Button';
import DashboardShot from '../DashboardShot/DashboardShot';

const ease = [0.22, 1, 0.36, 1];
const rise = (delay) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay, ease } });

export default function Hero() {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
    // the dashboard starts tilted back and stands up as you scroll
    const p = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.4 });
    const rotateX = useTransform(p, [0, 0.35], reduce ? [0, 0] : [22, 0]);
    const scale = useTransform(p, [0, 0.35], reduce ? [1, 1] : [0.9, 1]);
    const y = useTransform(p, [0, 0.35], reduce ? [0, 0] : [30, 0]);

    return (
        <section ref={ref} className={styles.hero}>
            <div className={styles.aurora} aria-hidden="true"><span /><span /><span /></div>
            <div className={styles.grid} aria-hidden="true" />

            <div className={styles.copy}>
                <motion.div {...rise(0)}>
                    <Link to="/features#packages" className={styles.chip}>
                        <span className={styles.chipNew}>New</span> Sell packages, track every visit <ArrowUpRight size={13} />
                    </Link>
                </motion.div>
                <motion.h1 {...rise(0.08)}>
                    <span className={styles.line}>More clients. Bigger bills.</span>
                    <span className={styles.line}>A brand they remember.</span>
                </motion.h1>
                <motion.p {...rise(0.16)}>
                    Hirsta helps your salon grow revenue, look premium at every touch and bring clients back again and again, with billing, memberships, packages and WhatsApp working together.
                </motion.p>
                <motion.div className={styles.actions} {...rise(0.24)}>
                    <Button to="/demo" variant="light" size="lg" icon={ArrowUpRight}>Grow my salon</Button>
                    <Button to="/#revenue" variant="ghostDark" size="lg" icon={PlayCircle}>See how it works</Button>
                </motion.div>
                <motion.ul className={styles.trust} {...rise(0.32)}>
                    {['Your logo on every bill', 'Clients brought back on WhatsApp', 'Cash collected in advance', 'Setup done with you'].map((t) => (
                        <li key={t}><Check size={13} strokeWidth={3} /> {t}</li>
                    ))}
                </motion.ul>
            </div>

            <div className={styles.stage}>
                <div className={styles.beam} aria-hidden="true" />
                <motion.div
                    className={styles.tilt}
                    style={{ rotateX, scale, y }}
                    initial={{ opacity: 0, y: 80 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 0.25, ease }}
                >
                    <DashboardShot />
                </motion.div>

            </div>
        </section>
    );
}
